<template>
	<AppDialog disableMaxWidth :width="dialogWidth" ref="dialog_container" dialogName="ChangeEmailTemplateDialog">
		<Row class="change-template-dialog pa-1">
			<Col class="left-side-container pt-1 ml-3">
				<Row class="left-header flex-justify-space-between width-100 mb-5 mt-1">
					<h1 class="font-size-lg font-weight-500 py-2 pr-2"><b>Email templates</b></h1>
					<AnemicButton small @click="createTemplate" class="cursor-pointer header-child btn mt-2 mr-4">+ Create a template</AnemicButton>
				</Row>
				<Col class="template-list overflow-y-auto">
					<div class="width-100" v-for="t in allTemplates" :key="`template-${t.id}`" :ref="`template-${t.id}`">
						<div class="text-colorTextSecondary mt-2 font-size-sm" v-if="t.isDivider">{{ t.title }}</div>
						<Row
							v-else
							@click="loadTemplate(t.id)"
							class="flex-justify-space-between cursor-pointer mt-2 template"
							:class="{ selected: checkIfIsSelectedTemplate(t.id) }"
						>
							<AppTextField
								@blur="onBlur(t.title, t.id)"
								@keyup.enter="onBlur(t.title, t.id)"
								:readonly="isTemplateFieldDisabled(t.id)"
								class="template-title width-100 text-center"
								:class="[isTemplateFieldDisabled(t.id) ? '' : 'text-underline']"
								flat
								dense
								hide-details
								v-model="t.title"
								solo
							/>
							<AppTooltip v-if="checkIfIsDefaultTemplate(t.id)" text="Default template"><AppIcon name="star" /></AppTooltip>
							<ActionsButton
								:template="t"
								@rename-template="renameTemplate"
								@duplicate-template="onDuplicateTemplate"
								@delete-template="onDeleteTemplateClicked"
								@set-default-template="onSetDefaultTemplate"
								@on-selected="() => loadTemplate(t.id)"
							/>
						</Row>
					</div>
				</Col>
			</Col>

			<Col id="right-side-container" class="width-100 pt-2">
				<Row class="right-header flex-justify-space-between width-100 pr-10">
					<span class="preview-title font-size-lg font-weight-500 header-child ml-2"
						><h2 class="truncate">{{ loadedTemplate?.title || '' }}</h2></span
					>
					<Row>
						<template v-if="!isLoadedTemplatePublic">
							<div class="header-child text-underline ml-1" v-if="numOfDynamicFields > 0">
								<Tooltip
									tooltipTitle="Dynamic fields"
									:activatorText="`${numOfDynamicFields} dynamic fields`"
									:tooltipItems="dynamicFields"
								/>
							</div>
							<div class="header-child pt-2 text-[14px] font-size-sm secondary-font-color" v-if="hasNumberOfFeaturesUsed">
								Used in {{ numOfFeaturesUsed }} features
							</div>
							<AnemicButton
								small
								@click="openEditTemplateDialog"
								:disabled="!loadedTemplate"
								class="header-child cursor-pointer btn edit ml-2 mt-1"
							>
								Edit
							</AnemicButton>
							<PrimaryButton class="select-btn ml-3 mt-1" small @click="selectTemplate" :disabled="!loadedTemplate">Select</PrimaryButton>

							<ActionsButton
								:template="loadedTemplate"
								:disabled="!loadedTemplate"
								@rename-template="renameTemplate"
								@duplicate-template="onDuplicateTemplate"
								@delete-template="onDeleteTemplateClicked"
								@set-default-template="onSetDefaultTemplate"
							/>
						</template>
						<template v-else>
							<PrimaryButton small @click="onDuplicatePublic" :disabled="!loadedTemplate" class="cursor-pointer btn duplicate mr-4 mt-1">
								Create usable copy
							</PrimaryButton>
						</template>
					</Row>
				</Row>
				<div class="main-template-content">
					<div id="editor" ref="emailEditor"></div>
				</div>
			</Col>
		</Row>
	</AppDialog>
</template>

<script>
import ActionsButton from './EmailTemplateReusables/ActionsButton.vue';
import Tooltip from '@/components/common/Tooltip.vue';
import AppDialog from '@/components/dialogs/AppDialog/AppDialog';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { APP_CONFIRMATION_DIALOG_RESPONSES } from '@/components/dialogs/AppDialogs/app-confirmation-dialog.const';
import {
	addTemplate,
	deleteTemplate,
	updateEmailTemplate,
	setSelectedEmailTemplate,
	fetchEmailTemplates,
	duplicateTemplate,
	fetchWorkflowsRelatedToTemplateId,
	setDefaultEmailTemplate,
	fetchAllEmailTemplates,
} from '@/store/modules/transactionalEmailTemplate/actions';
import {
	defaultEmailTemplate,
	useEmailTemplatesArr,
	usePublicEmailTemplate,
	usePublicEmailTemplatesArr,
} from '@/store/modules/transactionalEmailTemplate/getters';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';
import { extractStatusCode, STATUS_CODES } from '@/utils/error-handling-service';
import { isNullOrUndefined } from '@/utils/helpers';

export default {
	name: 'ChangeEmailTemplateDialog',
	components: { AppDialog, Tooltip, ActionsButton },
	props: { dialogPromiseHandlers: { type: Object }, templateIdInTabPreview: { type: Number } },
	async mounted() {
		await this.fetchAllEmailTemplates();
		if (this.emailEditor) {
			unlayer.init({
				id: 'editor',
				displayMode: 'email',
				projectId: 242660,
				customCSS: [
					` header { display: none; } .device-container > div:first-of-type { display: none; } 
					.device-container {
						border: unset; width: 100% !important; 
					} 
					.dNDMvF { padding: unset; }	
					`,
				],
			});
			this.setUnlayerIframeStyleIfNeeded();
			unlayer.addEventListener('editor:ready', this.onEditorReady);
			unlayer.addEventListener('design:loaded', this.onDesignLoaded);
		}
	},
	methods: {
		...mapActionsShorthand({
			setDefaultEmailTemplate,
			fetchAllEmailTemplates,
			setSelectedEmailTemplate,
			fetchEmailTemplates,
			addTemplate,
			deleteTemplate,
			updateEmailTemplate,
			duplicateTemplate,
			fetchWorkflowsRelatedToTemplateId,
		}),
		async onBlur(title, templateId) {
			if (!this.loadedTemplateId || !this.renameMode) {
				return;
			}
			// Makes sure to set the created template back to uneditable
			this.renameMode = false;
			try {
				await this.updateEmailTemplate({ id: templateId, payload: { title } });
			} catch (e) {
				this.showFailedToDeleteTemplateToast(e, 'rename');
			}
		},
		isTemplateFieldDisabled(templateId) {
			return !(templateId === this.loadedTemplateId && this.renameMode);
		},
		async createTemplate() {
			unlayer.loadBlank({ backgroundColor: 'white' });
			unlayer.saveDesign(async (design) => {
				const payload = { design, title: 'Template name', html: '' };
				const resp = await this.addTemplate(payload);
				this.loadedTemplateId = resp.id;
				this.showCursorOnTemplate();
			});
		},
		showCursorOnTemplate() {
			const refKey = `template-${this.loadedTemplateId}`;
			const selectedTemplate = this.$refs[refKey][0];
			const inputEl = selectedTemplate.querySelectorAll('input')[0];
			if (!inputEl) {
				console.error('No input element found under element:', selectedTemplate?.$el);
				return;
			}
			this.renameMode = true;
			inputEl.select();
		},
		checkIfIsSelectedTemplate(templateId) {
			return templateId === this.loadedTemplateId;
		},
		checkIfIsDefaultTemplate(templateId) {
			return templateId === this.defaultEmailTemplate?.id;
		},
		onDesignLoaded(data) {
			this.loadedDesign = data.design;
		},
		async onEditorReady() {
			this.loadedTemplateId = this.loadedTemplateId || this.templateIdInTabPreview;
			this.loadedDesign = this.loadedTemplate.design;
			unlayer.loadDesign(this.loadedTemplate.design);
			unlayer.showPreview('tablet');
			await this.calcUsedInFeatureInfo();
		},
		setUnlayerIframeStyleIfNeeded() {
			// Unlayer iframe has min-width: 1024px in it's style attribute.
			const mql1350 = window.matchMedia('(max-width: 1350px)');
			if (!mql1350.matches) {
				return;
			}
			const unlayerEditorIframe = document.querySelectorAll('#editor iframe')[0];
			unlayerEditorIframe.style.setProperty('min-width', '500px');
		},
		close() {
			AppDialogManagerService.closeCurrentDialog();
		},
		async loadTemplate(templateId) {
			this.loadedTemplateId = templateId;
			await this.calcUsedInFeatureInfo();
			unlayer.loadDesign(this.loadedTemplate.design);
		},
		async selectTemplate() {
			if (this.isLoadedTemplatePublic) {
				throw new Error('Public templates cannot be selected');
			}
			await this.setSelectedEmailTemplate({ templateId: this.loadedTemplateId });
			this.close();
		},
		async openEditTemplateDialog() {
			if (this.isPublicEmailTemplate) {
				this.$toast.error('Public templates cannot be edited');
				return;
			}
			this.close();
			const loadedTemplateTitle = this.loadedTemplate.title;
			const editTemplateDialogRes = await AppDialogManagerService.openEditEmailTemplateDialog({
				templateId: this.loadedTemplateId,
				templateTitle: loadedTemplateTitle,
			});
			if (!editTemplateDialogRes?.openChangeEmailTemplateDialog) {
				// Did not click on 'Go back (arrow) button'
				return;
			}
			await AppDialogManagerService.openChangeEmailTemplateDialog({ templateIdInTabPreview: this.loadedTemplateId });
		},
		renameTemplate(template) {
			if (this.usePublicEmailTemplate(template.id)) {
				this.$toast.error('Public templates cannot be edited');
				return;
			}
			this.loadedTemplateId = template.id;
			this.showCursorOnTemplate();
			this.loadTemplate(this.loadedTemplateId);
		},
		async onDuplicateTemplate({ id: templateId, isPublic }) {
			return await this.duplicateTemplate({ templateId, isPublic });
		},
		async onSetDefaultTemplate(templateId) {
			try {
				await this.setDefaultEmailTemplate({ templateId });
			} catch (e) {
				console.error('Failed to set default template', e);
				this.$toast.error('Failed to set default template, only account admins can set templates as default');
			}
			await this.fetchEmailTemplates();
		},
		async onDuplicatePublic() {
			const duplicatedTemplate = await this.onDuplicateTemplate({ id: this.loadedTemplateId, isPublic: this.isLoadedTemplatePublic });
			this.loadedTemplateId = duplicatedTemplate.id;
			this.loadTemplate(this.loadedTemplateId);
		},
		async onDeleteTemplateClicked(templateId) {
			if (this.usePublicEmailTemplate(templateId)) {
				this.$toast.error('Public templates cannot be deleted');
				return;
			}
			try {
				const res = await AppDialogManagerService.openConfirmationDialog({
					title: 'Delete template',
					description: 'Are you sure you want to delete this template?',
					yesText: 'Delete',
					noText: 'Cancel',
				});
				if (res == APP_CONFIRMATION_DIALOG_RESPONSES.YES) {
					const defaultDeleted = templateId === this.defaultEmailTemplate?.id;
					await this.deleteTemplate(templateId);
					if (this.loadedTemplateId === templateId) {
						// If the template being deleted is the one being viewed, load the default template
						if (defaultDeleted) {
							console.log('default deleted :)\n duplicate public and set as default?');
							this.close();
						} else {
							this.loadTemplate(this.defaultEmailTemplate.id);
						}
					}
				}
			} catch (e) {
				console.error('Failed to delete template', e);
				this.showFailedToDeleteTemplateToast(e, 'delete');
			}
		},
		showFailedToDeleteTemplateToast(error, verb) {
			if (extractStatusCode(error) === STATUS_CODES.FORBIDDEN) {
				this.$toast.error('You do not have permission to delete this template');
				return;
			}
			this.$toast.error(`Failed to ${verb} template`);
			return;
		},
		async calcUsedInFeatureInfo() {
			if (this.isLoadedTemplatePublic) {
				return;
			}
			try {
				this.usedInWorkflowsInfo = await this.fetchWorkflowsRelatedToTemplateId({
					templateId: this.loadedTemplateId,
				});
			} catch (e) {
				console.error('Failed to fetch workflows related to this template');
				this.usedInWorkflowsInfo = null;
			}
		},
	},
	computed: {
		...mapGettersShorthand({ defaultEmailTemplate, useEmailTemplatesArr, usePublicEmailTemplate, usePublicEmailTemplatesArr }),
		emailEditor() {
			return this.$refs.emailEditor;
		},
		allTemplates() {
			return [
				{ title: 'MY WORKSPACE TEMPLATES', isDivider: true, id: 'divider' },
				...this.privateTemplates.toReversed(),
				{ title: 'GALLERY', isDivider: true, id: 'divider-public' },
				...this.publicTemplates,
			];
		},
		loadedTemplate() {
			return this.allTemplates.find((t) => t.id === this.loadedTemplateId);
		},
		isLoadedTemplatePublic() {
			return this.loadedTemplate?.is_public;
		},
		privateTemplates() {
			return this.useEmailTemplatesArr;
		},
		publicTemplates() {
			return this.usePublicEmailTemplatesArr;
		},
		dynamicFields() {
			if (!this.loadedDesign?.contents) {
				return [];
			}
			const designContents = Object.values(this.loadedDesign.contents);
			const designText = designContents.map((content) => content.values.text).join(' ');
			const regexPattern = /\{\{(.+?)\}\}/g;
			return designText.match(regexPattern) || [];
		},
		numOfDynamicFields() {
			return this.dynamicFields?.length || 0;
		},
		isPublicEmailTemplate() {
			return this.usePublicEmailTemplate(this.loadedTemplateId);
		},
		numOfFeaturesUsed() {
			return this.usedInWorkflowsInfo?.count;
		},
		hasNumberOfFeaturesUsed() {
			return !isNullOrUndefined(this.numOfFeaturesUsed);
		},
	},
	data() {
		const mql1550 = window.matchMedia('(max-width: 1550px)');
		const dialogWidth = mql1550.matches ? '98vw' : '75vw';
		return {
			dialogWidth,
			loadedTemplateId: null,
			renameMode: false,
			loadedDesign: null,
			valToDisplay: '',
			usedInWorkflowsInfo: null,
		};
	},
};
</script>

<style scoped lang="scss">
.truncate {
	white-space: nowrap; /* Prevent line breaks */
	overflow: hidden; /* Hide overflow content */
	text-overflow: ellipsis; /* Display ellipsis when text overflows */
}

.preview-title {
	display: block; /* Ensure the span is treated as a block (necessary for overflow to work) */
	max-width: 100%; /* Ensure the span does not overflow its parent */
	@media (max-width: 1550px) {
		width: 30%;
	}
}
.selected {
	border-radius: 5px;
	background: rgba(242, 245, 247, 0.8);
}
.right-header {
	padding-bottom: 12px;
	border-bottom: 1px solid #e4e8f3;
}
.header-child {
	padding: 6px 7px;
	&.btn {
		border: solid 1px #6060f7;
		color: #6060f7;
		background-color: white !important;
		min-width: 80px;
		text-align: center;
		font-size: 14px !important;
		font-weight: 400 !important;
	}
	&.edit {
		width: 80px;
	}
}
.select-btn {
	padding: 6px 7px;
	min-width: 80px;
	text-align: center;
	font-size: 14px !important;
	font-weight: 400 !important;
}

.change-template-dialog {
	height: 850px;
	@media (max-width: 1550px) {
		height: 47rem !important;
	}
	@media (max-width: 1150px) {
		height: 38rem !important;
	}
	border-radius: 15px !important;
	box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.16) !important;
	padding: 20px 30px 14px 30px;

	::v-deep {
		.v-icon {
			font-size: 16px !important;
		}
		.v-input .v-input__control .v-input__slot input {
			cursor: pointer !important;
			font-size: 14px !important;
		}
		.selected {
			.v-input .v-input__control .v-input__slot {
				background: rgba(242, 245, 247, 0.8) !important;
			}
		}
	}

	.template {
		margin-right: 5%;
		border-radius: 5px;
		padding-left: 10px;
		::v-deep {
			// this affects the elements nested in ActionsButton comp
			.menu-button {
				opacity: 0;
				transition: 0.2s;
			}
			&:hover,
			&.selected {
				.menu-button {
					opacity: 1;
					transition: 0.2s;
				}
			}
		}
	}
}

.left-side-container {
	width: 33%;
	display: flex;
	flex-direction: column;
}
#right-side-container {
	border-left: 1px solid #e4e8f3;
}
#editor iframe {
	min-width: 100% !important;
}
.main-template-content {
	& > div {
		height: 49rem !important;
		@media (max-width: 1550px) {
			height: 43rem !important;
		}
		@media (max-width: 1150px) {
			height: 31rem !important;
		}
	}
}
</style>
